import { CopyIcon } from "lucide-react";

import { CheckIcon } from "lucide-react";
import { ButtonHTMLAttributes, FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";
import { stripAnchorTagsAndNormalizeNewlines } from "~/utils/markdownTerms";
import { Button } from "./ui/button";

const messages = defineMessages({
  copy: { id: "common.copy", defaultMessage: "Copy" },
  copyAll: { id: "common.copyAll", defaultMessage: "Copy All" },
});

type ContentCopyButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  content: string;
  size?: "sm" | "mini";
  color?: string;
  preCopyMessageId?: keyof typeof messages;
  eventName?: string;
  transformContent?: (content: string) => string;
};

export const ContentCopyButton: FC<ContentCopyButtonProps> = ({
  content,
  size = "mini",
  color = "foreground",
  preCopyMessageId = "copyAll",
  eventName,
  transformContent,
  ...props
}) => {
  const { isCopied, handleCopy } = useCopyContent();
  const intl = useIntl();

  const contentToUse = transformContent ? transformContent(content) : content;

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={() => {
        if (eventName) {
          trackEvent({ eventName });
        }
        const normalizedContent =
          stripAnchorTagsAndNormalizeNewlines(contentToUse);
        handleCopy(normalizedContent ?? "");
      }}
      disabled={isCopied}
      className={`text-${color} hover:text-${color} inline-flex items-center ${size === "mini" ? "px-0 text-xs hover:bg-transparent" : ""}`}
      {...props}
    >
      {isCopied ? (
        <>
          <CheckIcon className="w-4 h-4 mr-1" />
          {intl.formatMessage({
            id: "common.copied",
            defaultMessage: "Copied",
          })}
        </>
      ) : (
        <>
          <CopyIcon className="w-4 h-4 mr-1" />
          {intl.formatMessage(messages[preCopyMessageId])}
        </>
      )}
    </Button>
  );
};
